import { isEmpty } from 'radash';
import { useMemo, useRef, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';

import { Button } from '@/components/core/Button';
import { Grid } from '@/components/core/Grid';
import { fieldPathToGridStateKey } from '@/components/core/Grid/useGridPreferences';
import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useTranslation } from '@/hooks/useTranslation';
import { useUser } from '@/hooks/useUser';
import { logger } from '@/services/logger';
import { DataModelAttributeTypeName, Item } from '@/types/DataModel';

import useFocusOnAttribute from '../hooks/useFocusOnAttribute';
import { useFocusOnNewRow } from '../hooks/useFocusOnNewRow';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';
import { useIsHiddenAttribute } from '../hooks/useIsHiddenAttribute';
import { Layout } from '../Layout/Layout';
import { EditorComponent } from '../types';
import { generateCollectionAttributeColumns } from './columns';
import { filterCollection } from './filtering';
import { generateInitialSortingState } from './sorting';
import { useVisibleMemberAttributes } from './useMemberAttributes';

interface CollectionComponentProps extends EditorComponent {

}

interface CollectionDataType {
    [key: string]: string;
}

const CollectionComponent = ({
    attribute,
    direction ='vertical',
    fieldPath = attribute.name,
    hideLabel,
    hidden = false,
    readonly = false,
    isKeyAttribute,
    beforeAttributeControl
}: CollectionComponentProps) => {

    const { user } = useUser();
    const { organization } = useOrganization();
    const { t } = useTranslation();
    const { control, getValues, setValue, unregister, formState: { isDirty } } = useFormContext();

    const [enableSorting, setEnableSorting] = useState(true);
    const isAppendingRef = useRef(false);

    const focusRef = useFocusOnAttribute<HTMLDivElement>(attribute);

    const { fields } = useFieldArray({
        control,
        name: fieldPath,
    });

    // Enhanced remove function with proper form state management
    const remove = (index: number) => {
        unregister(`${fieldPath}.${index}`);
        // Get current values from the form
        const currentValues = getValues(fieldPath) || [];
        // Create new array without the item at the specified index
        const newValues = currentValues.filter((_: any, i: number) => i !== index);
        // Update the form state directly
        setValue(fieldPath, newValues, { shouldDirty: true });
    };

    const { dataModel } = useDataModel();

    hidden = useIsHiddenAttribute(attribute, attribute.name, hidden);

    readonly = useIsAttributeReadonly(attribute, attribute.name, readonly);

    const memberAttributes = useVisibleMemberAttributes(attribute, dataModel);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const columns = useMemo(() =>
        generateCollectionAttributeColumns(
            attribute,
            memberAttributes || [],
            fieldPath,
            fields,
            organization,
            remove,
            DataModelAttributeTypeName.Collection,
            user,
            [],
            readonly,
            t,
            getValues
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ), [fields, readonly, t, getValues, memberAttributes]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const initialSortingState = useMemo(() => generateInitialSortingState(attribute, memberAttributes || []), []);

    const filteredData = useMemo(() => {
        if (!dataModel || !organization || !user) {
            return fields;
        }

        const item = getValues() as Item;

        return filterCollection(fields, item, attribute, organization, user);
    }, [attribute, fields, dataModel, organization, user, getValues]);

    // Focus on the first column of newly added row
    useFocusOnNewRow({
        fields,
        fieldPath,
        memberAttributes,
        readonly,
    });

    if (!memberAttributes) {
        logger.error(`Collection: Value attribute ${attribute.name} has no members`);
        return null;
    }

    if (hidden) {
        return null;
    }

    const handleButtonClick = () => {

        if (isAppendingRef.current) {
            return;
        }
        isAppendingRef.current = true;
        setEnableSorting(false);
        // Get current form values
        const currentValues = getValues(fieldPath) || [];

        // Create new item and add it to the array
        const newItem = {};
        const newValues = [...currentValues, newItem];

        // Match setValue dirty propagation to current form dirty state
        setValue(fieldPath, newValues, { shouldDirty: isDirty });
        isAppendingRef.current = false;
    };

    return (
        <Layout
            attribute={attribute}
            hideLabel={hideLabel}
            direction={direction}
            fieldPath={fieldPath}
            gridMode={false}
            isKeyAttribute={isKeyAttribute}
            beforeAttributeControl={beforeAttributeControl}
            isClearable={false}
            className='tw-pb-4'
            actionComponent={!readonly ? <Button
                id={`${attribute.name}_button`}
                type="button"
                onClick={handleButtonClick}
            >
                {t('ADD_ENTRY')}
            </Button> : null}
        >
            {(readonly && isEmpty(filteredData) ) && <div className=
                'tw-border tw-border-gray-300 tw-rounded-[4px] tw-bg-[#dedede] tw-px-2 tw-py-2 tw-h-[34px] tw-mb-3'
            >
                {
                    t('GROUP.EMPTY')
                }
            </div>}
            { (!readonly || !isEmpty(filteredData)) && (
                <div ref={focusRef} tabIndex={-1} className="tw-flex tw-flex-col tw-gap-1 tw-w-full">
                    <Grid
                        id={attribute.name}
                        columns={columns}
                        insideItemEditor={true}
                        data={filteredData || []}
                        enableSorting={enableSorting}
                        enableRowSelection={false}
                        initialSortingState={initialSortingState}
                        storedStateKey={fieldPathToGridStateKey(fieldPath)}
                        className='tw-bg-white tw-p-0 tw-m-0 tw-max-h-[384px] tw-overflow-auto'
                        maxHeight = 'tw-max-h-[384px]'
                    />
                </div>
            )}
        </Layout>
    );
};

export { CollectionComponent, type CollectionDataType };
